/**
 * Singleton "playlist load progress" controller — purely observational.
 *
 * The actual loader lives in `playlistCache.js`; this file just exists so
 * any component can subscribe to "how many channels has the parser seen
 * so far?" without prop-drilling. Used by `PlayerLayout` to render a
 * visible progress bar instead of a frozen-looking splash on Smart TVs
 * with huge (500k+ item) IPTV playlists.
 */

let _state = { active: false, phase: "idle", done: 0, bytes: 0 };
const _listeners = new Set();

function emit() {
  const snap = { ..._state };
  for (const fn of _listeners) {
    try {
      fn(snap);
    } catch {
      /* listener crashes must not break the loader */
    }
  }
}

export function getLoadProgress() {
  return { ..._state };
}

export function subscribeToLoadProgress(listener) {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
}

/** Start of a fresh load — UI flips into the "loading huge playlist" mode. */
export function beginLoad() {
  _state = { active: true, phase: "fetch", done: 0, bytes: 0 };
  emit();
}

/** Called by the parser every 5000 items, or by the streaming fetcher
 *  for every chunk received. */
export function reportLoadProgress({ phase, done, bytes }) {
  _state = {
    active: true,
    phase,
    done: typeof done === "number" ? done : _state.done,
    bytes: typeof bytes === "number" ? bytes : _state.bytes,
  };
  emit();
}

/** Done / cancelled / failed — UI hides the progress overlay. */
export function endLoad() {
  _state = { active: false, phase: "idle", done: 0, bytes: 0 };
  emit();
}
